import React from 'react';
import { cn } from '@/utils/cn';

interface ProgressIndicatorProps {
  currentStep: number;
  totalSteps: number;
  steps: Array<{ label: string; description?: string }>;
  onStepClick?: (step: number) => void;
}

export const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  currentStep,
  totalSteps,
  steps,
  onStepClick,
}) => {
  return (
    <div className="w-full py-4">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const isCompleted = stepNumber < currentStep;
          const isCurrent = stepNumber === currentStep;
          const isClickable = onStepClick && stepNumber < currentStep;

          return (
            <React.Fragment key={stepNumber}>
              {/* Step Circle */}
              <div className="flex flex-col items-center relative flex-shrink-0">
                <button
                  onClick={() => isClickable && onStepClick(stepNumber)}
                  disabled={!isClickable}
                  className={cn(
                    'w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-semibold text-xs sm:text-sm transition-all',
                    'focus:outline-none focus:ring-2 focus:ring-offset-2',
                    {
                      'bg-blue-600 text-white': isCurrent,
                      'bg-green-600 text-white': isCompleted,
                      'bg-gray-200 text-gray-500': !isCurrent && !isCompleted,
                      'cursor-pointer hover:bg-green-700': isClickable,
                      'cursor-default': !isClickable,
                    }
                  )}
                  aria-label={`Step ${stepNumber}: ${step.label}`}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  {isCompleted ? (
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    stepNumber
                  )}
                </button>

                {/* Step Label - show current step on mobile, all steps on larger screens */}
                <div
                  className={cn('mt-2 text-center', {
                    'block': isCurrent,
                    'hidden sm:block': !isCurrent,
                  })}
                >
                  <p
                    className={cn('text-xs font-medium', {
                      'text-blue-600': isCurrent,
                      'text-gray-900': isCompleted,
                      'text-gray-500': !isCurrent && !isCompleted,
                    })}
                  >
                    {step.label}
                  </p>
                  {step.description && (
                    <p className="text-xs text-gray-400 mt-0.5 max-w-20">
                      {step.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Connector Line */}
              {index < totalSteps - 1 && (
                <div
                  className={cn('flex-1 h-0.5 mx-1 sm:mx-2 transition-colors min-w-2', {
                    'bg-green-600': isCompleted,
                    'bg-gray-200': !isCompleted,
                  })}
                  aria-hidden="true"
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
